import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { PageHeader } from '@/components/layout/PageHeader';
import { useAuth } from '@/lib/auth/AuthProvider';
import { cn } from '@/lib/utils/cn';
import { ADMIN_GUIDE_SECTIONS, type GuideSectionEntry } from '@/features/guides/sections';

export default function GuidesPage() {
  const { isAdmin } = useAuth();
  const [location] = useLocation();

  const sections = useMemo<GuideSectionEntry[]>(
    () => ADMIN_GUIDE_SECTIONS.filter((s) => !s.adminOnly || isAdmin),
    [isAdmin]
  );

  const initialHash = typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '';
  const defaultId = sections[0]?.id ?? 'welcome';
  const [activeId, setActiveId] = useState<string>(() =>
    sections.some((s) => s.id === initialHash) ? initialHash : defaultId
  );

  // When the user navigates via hash (back button, pasted link), sync state.
  useEffect(() => {
    function onHashChange() {
      const id = window.location.hash.replace('#', '');
      if (sections.some((s) => s.id === id)) setActiveId(id);
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [sections]);

  // If the filtered sections list changed (e.g. role loaded), make sure the
  // active id still resolves to something.
  useEffect(() => {
    if (!sections.some((s) => s.id === activeId)) setActiveId(defaultId);
  }, [sections, activeId, defaultId]);

  function selectSection(id: string) {
    setActiveId(id);
    if (typeof window !== 'undefined') {
      const url = `${location}#${id}`;
      window.history.replaceState(null, '', url);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  const ActiveComponent =
    sections.find((s) => s.id === activeId)?.component ?? sections[0]?.component;

  return (
    <>
      <PageHeader
        eyebrow="Help centre"
        title="Guides"
        description="Step-by-step walkthroughs for everything in the portal, plus a shareable guide for your candidates."
      />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[260px,1fr]">
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <nav className="glass-panel flex flex-col gap-1 p-2">
            {sections.map((section) => {
              const active = section.id === activeId;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => selectSection(section.id)}
                  className={cn(
                    'group flex w-full flex-col items-start rounded-xl px-3 py-2.5 text-left transition',
                    active
                      ? 'bg-white/10 text-white shadow-glow-soft'
                      : 'text-muted-foreground hover:bg-white/5 hover:text-white'
                  )}
                >
                  <span className="text-sm font-medium">{section.label}</span>
                  <span
                    className={cn(
                      'mt-0.5 text-[11px]',
                      active ? 'text-neurax-electric/80' : 'text-muted-foreground/70'
                    )}
                  >
                    {section.subtitle}
                  </span>
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="min-w-0">{ActiveComponent && <ActiveComponent />}</div>
      </div>
    </>
  );
}
